import React, { useState, useEffect } from 'react';
import ServiciosCard from '../components/cliente/ServiciosCard';
import { getServiciosPublicos } from '../api/servicioService';
import { getDoctoresDisponibles } from '../api/doctorService';
import imagenConsulta from '../data/consulta_oftalmologica.webp';
import imagenGlaucoma from '../data/control-glaucoma.jpg';
import imagenLentes from '../data/examen-lentes.jpg';

// Configuración de la URL base para imágenes
const API_BASE_URL =
  process.env.REACT_APP_API_ROOT_URL
  || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : '');

const DOCTORA_NOMBRE_MOSTRAR = 'Ximena';
const DOCTORA_APELLIDO_MOSTRAR = 'Rios';
const DOCTORA_IMAGEN_REMOTA = 'https://draximenarios.com/wp-content/uploads/2024/02/Dra-Ximena-Rios-Cirujana-Oftalmica-Oftalmologia-en-Quito-Especialista-02.png.webp';
const DOCTORA_IMAGEN_FALLBACK = '/data/Dra-Ximena-Rios.webp';

const normalizarTexto = (texto = '') =>
  texto
    .toString()
    .normalize('NFD')
    .replace(/[^\w\s]/g, '')
    .toLowerCase()
    .trim();

const seleccionarDoctoraPrincipal = (doctores = []) => {
  if (!Array.isArray(doctores) || doctores.length === 0) return null;

  const doctoraXimena = doctores.find((doctor) => {
    const nombreCompleto = `${doctor?.nombre || ''} ${doctor?.apellido || ''}`;
    return normalizarTexto(nombreCompleto).includes('ximena rios');
  });

  return doctoraXimena || doctores[0];
};

const obtenerImagenLocalServicio = (nombreServicio = '') => {
  const nombre = nombreServicio.toLowerCase();

  if (nombre.includes('consulta')) return imagenConsulta;
  if (nombre.includes('glaucoma')) return imagenGlaucoma;
  if (nombre.includes('lente') || nombre.includes('vista')) return imagenLentes;

  return null;
};

const Servicios = () => {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doctores, setDoctores] = useState([]);
  const [doctorSeleccionado, setDoctorSeleccionado] = useState(null);
  const [cargandoDoctores, setCargandoDoctores] = useState(true);

  // Primero cargamos la lista de doctores
  useEffect(() => {
    const cargarDoctores = async () => {
      try {
        setCargandoDoctores(true);
        const response = await getDoctoresDisponibles();
        console.log('Doctores disponibles:', response.data);

        if (response.data && response.data.data && response.data.data.length > 0) {
          const doctoraPrincipal = seleccionarDoctoraPrincipal(response.data.data);

          setDoctores(doctoraPrincipal ? [doctoraPrincipal] : []);
          setDoctorSeleccionado(doctoraPrincipal);
        } else {
          setError('No hay doctores disponibles en este momento');
        }
      } catch (err) {
        console.error('Error al cargar doctores:', err);
        setError('No se pudieron cargar los doctores disponibles');
      } finally {
        setCargandoDoctores(false);
      }
    };

    cargarDoctores();
  }, []);

  // Luego cargamos los servicios cuando tengamos un doctor seleccionado
  useEffect(() => {
    if (!doctorSeleccionado) return; // No hacemos nada si no hay doctor seleccionado

    const cargarServicios = async () => {
      try {
        setLoading(true);

        // Usar el ID del doctor seleccionado
        const doctorId = doctorSeleccionado.id;
        console.log('Cargando servicios para el doctor:', doctorId);

        const response = await getServiciosPublicos(doctorId);
        console.log('Respuesta API:', response.data); // Para debugging

        // Verificar estructura de la respuesta
        if (response.data && response.data.data && response.data.data.servicios) {
          const serviciosProcesados = response.data.data.servicios.map(servicio => {
            const imagenRelativa = servicio.imagen || '';
            const imagenRemota = imagenRelativa.startsWith('http')
              ? imagenRelativa
              : (API_BASE_URL ? `${API_BASE_URL}${imagenRelativa}` : imagenRelativa);
            const imagenLocal = obtenerImagenLocalServicio(servicio.nombre || servicio.titulo || '');

            return {
              ...servicio,
              doctorId: response.data.data.doctor.id,
              imagen: imagenLocal || imagenRemota,
              titulo: servicio.nombre,
            };
          });
          setServicios(serviciosProcesados);

          setError(null);
        } else {
          throw new Error('Formato de respuesta inesperado');
        }
      } catch (err) {
        console.error('Error al cargar servicios:', err);
        setError('No se pudieron cargar los servicios. Por favor, intente nuevamente más tarde.');

        // Usar datos estáticos como respaldo
        try {
          const modServicios = await import('../data/servicios');
          if (modServicios && modServicios.servicios) {
            console.log('Cargando servicios desde datos estáticos');
            setServicios(modServicios.servicios);
          }
        } catch (importError) {
          console.error('Error al cargar servicios estáticos:', importError);
        }
      } finally {
        setLoading(false);
      }
    };

    cargarServicios();
  }, [doctorSeleccionado]);

  // Mostrar indicador de carga mientras se cargan los doctores
  if (cargandoDoctores) {
    return (
      <div style={{ padding: '50px', textAlign: 'center', backgroundColor: '#f5f7fa', height: '100vh' }}>
        <div style={{
          border: '4px solid rgba(106, 90, 205, 0.1)',
          borderTop: '4px solid #6a5acd',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{ color: '#6a5acd', fontWeight: '500' }}>Cargando sistema de citas...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Mostrar indicador de carga mientras se cargan los servicios
  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center', backgroundColor: '#f5f7fa', height: '100vh' }}>
        <div style={{
          border: '4px solid rgba(106, 90, 205, 0.1)',
          borderTop: '4px solid #6a5acd',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{ color: '#6a5acd', fontWeight: '500' }}>Cargando servicios disponibles...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error && servicios.length === 0) {
    return (
      <div style={{
        padding: '30px',
        textAlign: 'center',
        color: '#e74c3c',
        backgroundColor: '#f5f7fa',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxWidth: '500px'
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#e74c3c', marginBottom: '15px' }}></i>
          <h3>Error al cargar servicios</h3>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: '#6a5acd',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '20px',
              fontWeight: 'bold'
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0', display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Cabecera del sistema */}
      <div style={{
        backgroundColor: '#fff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ color: '#6a5acd', margin: 0 }}>Sistema de Citas Médicas</h2>
          <p style={{ margin: '5px 0 0 0', color: '#888' }}>Seleccione un servicio para agendar su cita</p>
        </div>
        <div style={{
          backgroundColor: '#6a5acd',
          color: 'white',
          padding: '10px 15px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(106, 90, 205, 0.3)',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center'
        }}>
          <i className="fas fa-calendar-alt" style={{ marginRight: '8px' }}></i>
          Agendar Cita
        </div>
      </div>

      <div style={{ padding: '40px', flex: 1 }}>
        {/* Tarjeta principal */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          padding: '30px',
          marginBottom: '30px'
        }}>
          <h2 style={{
            color: '#444',
            marginTop: '0',
            marginBottom: '25px',
            fontSize: '22px',
            borderBottom: '1px solid #eee',
            paddingBottom: '15px'
          }}>
            Servicios Disponibles
          </h2>

          {/* Mostrar información del doctor seleccionado */}
          {doctorSeleccionado && doctores.length > 0 && (
            <div style={{
              backgroundColor: '#f0f3fb',
              padding: '20px',
              borderRadius: '6px',
              marginBottom: '30px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <img
                src={DOCTORA_IMAGEN_REMOTA}
                alt={`Dr(a). ${DOCTORA_NOMBRE_MOSTRAR} ${DOCTORA_APELLIDO_MOSTRAR}`}
                onError={(event) => {
                  if (event.currentTarget.dataset.fallbackApplied === 'true') {
                    return;
                  }

                  event.currentTarget.dataset.fallbackApplied = 'true';
                  event.currentTarget.src = DOCTORA_IMAGEN_FALLBACK;
                }}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  marginRight: '15px',
                  boxShadow: '0 0 4px rgba(0,0,0,0.1)'
                }}
              />
              <div>
                <h3 style={{ margin: '0 0 5px 0', color: '#444' }}>
                  Dr(a). {DOCTORA_NOMBRE_MOSTRAR} {DOCTORA_APELLIDO_MOSTRAR}
                </h3>
                <p style={{ color: '#666', margin: 0 }}>
                  <span style={{
                    backgroundColor: '#6a5acd',
                    color: 'white',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {doctorSeleccionado.especialidad}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Lista de servicios */}
          {servicios.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f9f9ff',
              borderRadius: '6px',
              color: '#666'
            }}>
              <i className="fas fa-info-circle" style={{ fontSize: '32px', color: '#6a5acd', marginBottom: '15px' }}></i>
              <p>No hay servicios disponibles en este momento.</p>
              <p>Por favor intente más tarde o contacte al administrador.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px' }}>
              {servicios.map(servicio => (
                <ServiciosCard key={servicio.id} servicio={servicio} />
              ))}
            </div>
          )}
        </div>

        {/* Instrucciones */}
        <div style={{
          backgroundColor: '#f0f3fb',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid #e0e4f1'
        }}>
          <h3 style={{ color: '#6a5acd', marginTop: '0' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
            ¿Cómo agendar una cita?
          </h3>
          <ol style={{ color: '#555', paddingLeft: '25px' }}>
            <li style={{ marginBottom: '8px' }}>Revise los servicios disponibles con la Dra. Ximena Rios</li>
            <li style={{ marginBottom: '8px' }}>Elija el servicio médico que necesita</li>
            <li style={{ marginBottom: '8px' }}>Haga clic en "Agendar" en el servicio deseado</li>
            <li style={{ marginBottom: '8px' }}>Complete el formulario con sus datos personales</li>
            <li>Confirme su cita y guarde el comprobante</li>
          </ol>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#6a5acd',
        color: 'white',
        padding: '15px 0',
        textAlign: 'center',
        marginTop: 'auto'
      }}>
        <p style={{ margin: 0 }}>Copyright 2025 - BM Creatividad</p>
      </footer>
    </div>
  );
};

export default Servicios;